import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';

// Suppress a noisy extension-injected error where an injected vendor script
// emits `No Listener: tabs:outgoing.message.ready`. This registers a narrow
// global handler for the unhandled promise rejection and prevents the
// uncaught error from flooding devtools while leaving other errors visible.
if (typeof window !== 'undefined' && window.addEventListener) {
	window.addEventListener('unhandledrejection', (ev) => {
		try {
			const reason = ev && ev.reason;
			const msg = reason && (reason.message || String(reason));
			if (msg && msg.includes && msg.includes('No Listener: tabs:outgoing.message.ready')) {
				// quiet this specific extension error in devtools
				// eslint-disable-next-line no-console
				console.warn('Suppressed extension error: tabs:outgoing.message.ready');
				try { ev.preventDefault(); } catch (_) {}
			}
		} catch (e) {
			/* ignore */
		}
	});

	// Also register a global error handler to catch the same message when it
	// surfaces as a synchronous error event in some environments/extensions.
	window.addEventListener('error', (ev) => {
		try {
			const msg = ev && (ev.message || String(ev.error || ev.reason || ''));
			if (msg && msg.includes && msg.includes('No Listener: tabs:outgoing.message.ready')) {
				// quiet this specific extension error
				// eslint-disable-next-line no-console
				console.warn('Suppressed extension error (error event): tabs:outgoing.message.ready');
				try { ev.preventDefault(); } catch (_) {}
				return true;
			}
		} catch (e) {
			/* ignore */
		}
		return false;
	});
}

const root = createRoot(document.getElementById('root'));
// App root boundary: a render crash anywhere in App.js (like #65's
// `platform()` ReferenceError on Settings -> Agent) shows a full-screen
// fallback with Reload + copy-error instead of unmounting the whole tree
// to a blank page. See components/ErrorBoundary.js for what it can/can't catch.
root.render(
  <ErrorBoundary label="app">
    <App />
  </ErrorBoundary>
);
